import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  Switch,
  Typography
} from "@mui/material";
import DuplicateAlert from "../../shared/create/DuplicateAlert";
import IndividualInformation from "./OfficeInformation";
import { AddEditContactContext } from "./AddEditContactContext";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import CompanyInformation from "./CompanyInformation";
import { useNavigate } from "react-router-dom";
import { useSettingsContext } from "../../../contexts/SettingsContext";
import { Helmet } from "react-helmet-async";
import GoBackButton from "../../shared/create/GoBackButton";
import AddNewBranchToCompany from "./AddNewBranchToCompany";
import { Company, ContactRole, DynamicsContact, FullContact, Phone, SuspectedMatch } from "../../types";
import axios from "axios";
import DuplicateContact from "../../shared/create/DuplicateContact";
import AddEditCompanyToPhoneBook from "./AddEditCompanyToPhoneBook";
import About from "../../company/profile/About";
import RoleAlert from "./RoleAlert";
import MissingCompanyBranchAlert from "./MissingCompanyBranchAlert";
import PersonalInformation from "./PersonalInformation";
import { closeIFrame } from "../../../utils/iframeUtils";
import { logEvent } from "../../../utils/analyticsLogger";
import Discrepancies from "../merge/Discrepancies";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import useResponsive from "../../../../minimals/hooks/useResponsive";
import { UserContext } from "../../../contexts/UserContext";
import MergedOrdersModal from "./MergedOrdersModal";
import Iconify from "../../../../minimals/components/iconify";
import { ContactOrder } from "../../shared/ordersAndInvoices/types";

export default function AddEditContactForm() {
  const isMobile = useResponsive("down", "md");
  const outerTheme = useTheme();

  // This form is spread over half a dozen components and ~60 inputs, so rather
  // than touching each one, mobile renders the whole tree under a theme that
  // defaults them to small + full width and trims the card padding. Desktop
  // renders the untouched tree, with no provider in the way at all.
  const mobileFormTheme = useMemo(
    () =>
      createTheme(outerTheme, {
        components: {
          MuiTextField: { defaultProps: { size: "small", fullWidth: true } },
          MuiFormControl: { defaultProps: { size: "small", fullWidth: true } },
          MuiAutocomplete: { defaultProps: { size: "small", fullWidth: true } },
          MuiSelect: { defaultProps: { size: "small" } },
          MuiButton: { defaultProps: { size: "small" } },
          MuiCardHeader: { styleOverrides: { root: { padding: "16px 16px 8px" } } },
          MuiCardContent: {
            styleOverrides: { root: { padding: 16, "&:last-child": { paddingBottom: 16 } } }
          }
        }
      }),
    [outerTheme]
  );

  const {
    submit,
    submitting,
    newCompany,
    editMode,
    newBranch,
    contact,
    reset,
    showInvalidAlert,
    setShowInvalidAlert,
    validationErrorMessage,
    setValidationErrorMessage,
    noCompanyMode,
    isBranchWithoutCompany,
    company,
    showRoleAlert,
    setShowRoleAlert,
    setAllowNoRole,
    showMissingCompanyBranchAlert,
    setShowMissingCompanyBranchAlert,
    setUseDefaultCompanyBranch,
    mergingContacts,
    setMergingContacts,
    handleMergeAndDelete,
    showOrdersModal,
    setShowOrdersModal,
    merging,
    getAllContactInfo,
    mergedOrderResult,
    getOrdersForLookupCode,
    getOrdersToMerge,
    deactivateContact,
    handleContactChange,
    isEmbedded,
    cancelEmbedded
  } = useContext(AddEditContactContext);

  const { user } = useContext(UserContext);
  const [theInput, setTheInput] = useState("");
  const ref = useRef(null);
  const { themeStretch, isAppInIFrame } = useSettingsContext();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const contactsIdsToMerge = useMemo(() => searchParams.getAll("ids").map(Number), [location.search]);
  const [duplicateClientList, setDuplicateClientList] = useState<SuspectedMatch[]>([]);
  const [emailExistsError, setEmailExistsError] = useState(false);
  const [personalEmailExistsError, setPersonalEmailExistsError] = useState(false);
  const [duplicateCompanyList, setDuplicateCompanyList] = useState<Company[]>([]);
  const [isHovered, setIsHovered] = useState(false);

  const setUpMergingContacts = async (firstContactId: number, secondContactId: number) => {
    const contactsPromises = [getAllContactInfo(firstContactId), getAllContactInfo(secondContactId)];
    const [contact1, contact2] = await Promise.all(contactsPromises);

    const getOrderCount = async (lookupCode: string) => {
      const { data } = await axios.get(
        `/api/clientphonebook/contacts/getcontactordersbylookupcode?lookupCode=${lookupCode}`
      );

      return data;
    };

    const contact1OrderCount = (await getOrderCount(contact1.lookupCode)).length;
    const contact2OrderCount = (await getOrderCount(contact2.lookupCode)).length;

    const arr = contact1OrderCount > contact2OrderCount ? [contact1, contact2] : [contact2, contact1];
    setMergingContacts(arr);
  };
  const searchClientDuplicates = async () => {
    const contactPhones = contact.phoneNumbers?.map((p) => p.number || "") || [];
    const contactRolePhones = contact.contactRoles.flatMap((cr) => cr.phoneNumbers.map((p) => p.number || ""));
    const phoneNumbers = contactPhones.concat(contactRolePhones).filter((n) => n.length > 3);

    if ((contact.firstName && contact.lastName) || contact.contactRoles[0]?.businessEmail || phoneNumbers.length) {
      const { data: clientphonebookContacts } = await axios.post("/api/clientphonebook/contacts/findMatching", {
        id: contact.id || null,
        firstName: (contact.firstName || "").trim(),
        lastName: (contact.lastName || "").trim(),
        businessEmail: (contact.contactRoles[0]?.businessEmail || "").trim(),
        phoneNumbers
      });
      const clientPhonebookContacts = clientphonebookContacts.map((d: FullContact) => ({
        ...d,
        isDynamicContact: false
      }));

      const queryParams = new URLSearchParams({
        firstName: (contact.firstName || "").trim(),
        lastName: (contact.lastName || "").trim(),
        email: (contact.contactRoles[0]?.businessEmail || "").trim()
      });

      const removeContactsWithMatchingLookupCodes = (
        dynamicsContacts: DynamicsContact[],
        phonebookContacts: FullContact[]
      ) => {
        const removedMatchingContacts = dynamicsContacts.filter((dc) =>
          phonebookContacts.some((pc) => dc.lookupCode !== pc.lookupCode)
        );
        return removedMatchingContacts;
      };
      const { data } = await axios.get(`/proxy/api/dynamics/contacts/Searchv2?${queryParams}`);

      let dynamics = data.map((d: DynamicsContact) => ({
        dynamicsGuid: d.id,
        firstName: d.firstName,
        lastName: d.lastName,
        address1: d.address,
        city: d.city,
        state: d.state,
        zip: d.zip,
        contactRoles: [
          {
            phoneNumbers: [
              {
                type: "Business",
                number: d.phone
              }
            ],
            businessEmail: d.emailAddress
          }
        ],
        isDynamicsContact: true
      }));
      dynamics = removeContactsWithMatchingLookupCodes(dynamics, clientPhonebookContacts);
      if (contact?.id != null) {
        dynamics = dynamics.filter((d: any) => d?.dynamicsGuid !== contact?.dynamicsGuid);
      }

      const combinedContacts = clientPhonebookContacts.concat(dynamics);
      setDuplicateClientList(combinedContacts);
      return;
    }

    setDuplicateClientList([]);
  };

  const searchCompanyDuplicates = async () => {
    const { data } = await axios.post("/api/clientphonebook/companies/findMatching", {
      id: company.id,
      name: company.name || "",
      website: company.website || "",
      phone: company.branches[0]?.phone || "",
      email: company.branches[0]?.email || "",
      fax: company.branches[0]?.fax || "",
      addresses: [
        {
          address1: company.branches[0]?.address1 || "",
          state: company.branches[0]?.state || ""
        }
      ]
    });
    const duplicateCompanies =
      data?.map(
        (d: any) =>
          ({
            ...d.company,
            branches: [d]
          }) as Company
      ) || [];
    setDuplicateCompanyList(duplicateCompanies);
  };

  const handleDismiss = () => {
    setDuplicateClientList([]);
    setDuplicateCompanyList([]);
  };

  const searchWords = [
    company.name,
    company.website,
    company.branches[0]?.phone?.replace("+", ""),
    company.branches[0]?.email,
    company.branches[0]?.address1
  ].filter((w) => w) as string[];

  const hasExistingBranch = !!contact.contactRoles[0]?.companyBranchId;

  const handleRoleAlertNoClick = () => {
    setAllowNoRole(true);
    setShowRoleAlert(false);
  };

  const handleRoleAlertClose = () => setShowRoleAlert(false);

  const handleMissingCompanyBranchAlertNoClick = () => {
    setUseDefaultCompanyBranch(true);
    setShowMissingCompanyBranchAlert(false);
  };

  const handleMissingCompanyBranchAlertClose = () => setShowMissingCompanyBranchAlert(false);

  const saveButton = () => {
    if (mergingContacts.length > 0) {
      return merging ? "Merging..." : "Complete Merge";
    } else {
      return submitting ? "Saving..." : isAppInIFrame || isEmbedded ? "Save & Add to Order" : "Save";
    }
  };

  const mergeContactOrder = async (orderToMerge: ContactOrder) => {
    const { data } = await axios.post("/api/clientphonebook/contacts/updateorderlookup", {
      contactOrder: orderToMerge,
      lookupCodeToDelete: mergingContacts[1].lookupCode,
      contactToKeep: mergingContacts[0]
    });
    return data;
  };

  const onCancelClick = () => {
    reset();
    if (isEmbedded) {
      cancelEmbedded?.();
      return;
    }
    if (isAppInIFrame) {
      closeIFrame();
    }
    navigate(
      editMode && contact.id ? `../phonebook/contact/profile?id=${contact.id}` : "../phonebook/contacts/individuals"
    );
  };

  useEffect(() => {
    if (contactsIdsToMerge.length < 2) {
      return;
    }
    setUpMergingContacts(contactsIdsToMerge[0], contactsIdsToMerge[1]);
  }, [contactsIdsToMerge]);

  useEffect(() => {
    const savedRef: any = ref.current;
    if (savedRef) {
      savedRef.focus();
      savedRef.style.border = "#1817A8";
      savedRef.style.background = "#E6E6FF";
      // savedRef.scrollIntoView({
      //   behavior: "smooth"
      // })
    }
    return () => {
      if (savedRef) {
        savedRef.style.background = "white";
      }
    };
  }, [theInput]);
  return (
    <>
      {isAppInIFrame && (
        <Stack direction="row" justifyContent="flex-end" paddingRight={1.5} marginBottom={1}>
          <IconButton
            size="large"
            onClick={closeIFrame}
            sx={{ position: "absolute", top: 16, right: 16, padding: 0.3 }}
          >
            <Iconify icon="eva:close-outline" width={32} height={32} />
          </IconButton>
        </Stack>
      )}
      <Helmet>
        <title>
          {editMode ? "Edit Contact" : mergingContacts.length > 0 ? "Merge Contacts" : "Create a Contact"} | TitleQ
        </title>
      </Helmet>

      <ThemeProvider theme={isMobile ? mobileFormTheme : outerTheme}>
        <Container maxWidth={isEmbedded ? false : themeStretch ? false : "lg"}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom={{ xs: 2, md: 4 }}>
            <Typography variant={isMobile ? "h5" : "h4"}>
              {editMode ? "Edit Contact" : mergingContacts.length > 0 ? "Merge Contacts" : "Create a Contact"}
            </Typography>
            <GoBackButton customClickHandler={onCancelClick} />
          </Stack>
          <Grid container marginTop={{ xs: 2, md: 5 }} columnSpacing={3} rowSpacing={{ xs: 3, md: 0 }}>
            {(duplicateClientList?.length > 0 || duplicateCompanyList?.length > 0) && mergingContacts.length < 2 && (
              <Grid item xs={12} marginBottom={5}>
                <DuplicateAlert handleDismiss={handleDismiss} />
              </Grid>
            )}

            <Grid item xs={12} md={8}>
              <Stack spacing={3}>
                <IndividualInformation
                  refProp={ref}
                  theInput={theInput}
                  setTheInput={setTheInput}
                  searchClientDuplicates={searchClientDuplicates}
                  emailExistsError={emailExistsError}
                  setEmailExistsError={setEmailExistsError}
                  isMerge={mergingContacts.length > 0}
                />
                {!noCompanyMode && !newCompany && !newBranch && (
                  <CompanyInformation
                    refProp={ref}
                    theInput={theInput}
                    setDuplicateCompanyList={setDuplicateCompanyList}
                  />
                )}
                {(newCompany || noCompanyMode) && (
                  <AddEditCompanyToPhoneBook searchCompanyDuplicates={searchCompanyDuplicates} />
                )}
                {newBranch && <AddNewBranchToCompany />}
                <PersonalInformation
                  refProp={ref}
                  theInput={theInput}
                  personalEmailExistsError={personalEmailExistsError}
                  setPersonalEmailExistsError={setPersonalEmailExistsError}
                  isMerge={mergingContacts.length > 0}
                />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  {!editMode && (
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={contact?.isLead ?? false}
                          onChange={(e) => handleContactChange("isLead", e.target.checked)}
                        />
                      }
                      label="Lead"
                      sx={{ color: "text.secondary" }}
                    />
                  )}
                  <Stack direction="row" spacing={2} marginLeft="auto">
                    <Button
                      variant="soft"
                      color="error"
                      onClick={onCancelClick}
                      disabled={
                        newCompany || (isBranchWithoutCompany && editMode && !noCompanyMode && !hasExistingBranch)
                      }
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        mergingContacts.length > 0
                          ? handleMergeAndDelete(mergingContacts[1].lookupCode, mergingContacts[1].id)
                          : submit();
                        logEvent("Phonebook", `${editMode ? "Edit new individual" : "Add new individual"}`);
                      }}
                      disabled={
                        (newCompany && !noCompanyMode) ||
                        (isBranchWithoutCompany && editMode && !noCompanyMode && !hasExistingBranch) ||
                        submitting ||
                        (emailExistsError && mergingContacts.length === 0) ||
                        (personalEmailExistsError && mergingContacts.length === 0)
                      }
                    >
                      {saveButton()}
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={3}>
                {mergingContacts.length > 0 && <Discrepancies setTheInput={setTheInput} />}
                {(duplicateClientList?.length > 0 || duplicateCompanyList?.length > 0) && (
                  <Card
                    sx={{
                      p: 2,
                      mb: 3,
                      position: "sticky",
                      top: 62,
                      maxHeight: "100vh",
                      overflowY: "scroll"
                    }}
                  >
                    <Stack spacing={2}>
                      {duplicateClientList.length > 0 &&
                        mergingContacts.length < 2 &&
                        duplicateClientList.map((dcl) => (
                          <Grid key={dcl.contact.id}>
                            <DuplicateContact duplicateClient={dcl} />
                          </Grid>
                        ))}
                      {duplicateCompanyList.length > 0 &&
                        duplicateCompanyList.map((dcl) => (
                          <Grid key={dcl.id}>
                            <About company={dcl} duplicateMode searchWords={searchWords} />
                          </Grid>
                        ))}
                    </Stack>
                  </Card>
                )}
              </Stack>
            </Grid>
          </Grid>
          {showInvalidAlert && (
            <Snackbar
              open={showInvalidAlert}
              onClose={() => setShowInvalidAlert(false)}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Alert
                onClose={() => setShowInvalidAlert(false)}
                severity="error"
                variant="filled"
                sx={{ width: "100%" }}
              >
                A{" "}
                <Typography variant="body2" fontWeight={900} display="inline-block">
                  Last Name
                </Typography>{" "}
                and{" "}
                <Typography variant="body2" fontWeight={900} display="inline-block">
                  ONE
                </Typography>
                {"…. "}
                of the following is required:
                <Typography variant="body2">• Email</Typography>
                <Typography variant="body2">• A Phone #</Typography>
                <Typography variant="body2">• A Company</Typography>
                <Typography variant="body2">• An Address</Typography>
              </Alert>
            </Snackbar>
          )}
          <Snackbar
            open={!!validationErrorMessage}
            onClose={() => setValidationErrorMessage(null)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={() => setValidationErrorMessage(null)}
              severity="error"
              variant="filled"
              sx={{ width: "100%" }}
            >
              {validationErrorMessage}
            </Alert>
          </Snackbar>
        </Container>
      </ThemeProvider>
      {showOrdersModal && (
        <MergedOrdersModal
          mergedOrderResult={mergedOrderResult}
          getOrdersForLookupCode={getOrdersForLookupCode}
          updateOrders={getOrdersToMerge}
          mergeOrder={mergeContactOrder}
          deleteingLookupCodes={[mergingContacts[1].lookupCode]}
          lookupCodeToKeep={mergingContacts[0].lookupCode}
          deactivateLookupCode={deactivateContact}
          navigateToProfile={`../phonebook/contact/profile?id=${contact.id}`}
        />
      )}
      {showRoleAlert && !showInvalidAlert && (
        <RoleAlert handleClose={handleRoleAlertClose} handleNo={handleRoleAlertNoClick} />
      )}
      {showMissingCompanyBranchAlert && (
        <MissingCompanyBranchAlert
          handleClose={handleMissingCompanyBranchAlertClose}
          handleNo={handleMissingCompanyBranchAlertNoClick}
        />
      )}
    </>
  );
}
